<template>
  <div
    v-if="!item.meta || !item.meta.hidden"
    :class="[
      (isCollapse && isFirstLevel) ? 'collapse-mode' : 'expand-mode',
      { 'first-level': isFirstLevel },
      `menu-level-${menuLevel}`
    ]"
  >
    <template
      v-if="!alwaysShowRootMenu && theOnlyOneChild && (!theOnlyOneChild.children || !theOnlyOneChild.children.length)"
    >
      <el-menu-item
        :index="resolvePath(theOnlyOneChild.path)"
        :class="{ 'submenu-title-noDropdown': isFirstLevel }"
        @click="handleMenuClick(resolvePath(theOnlyOneChild.path))"
      >
        <i class="iconfont" v-if="theOnlyOneChild.meta.icon" :class="theOnlyOneChild.meta.icon" :title="(isCollapse && isFirstLevel) ? theOnlyOneChild.meta.title : ''"></i>
        <span v-if="theOnlyOneChild.meta.title">{{ theOnlyOneChild.meta.title}} </span>
      </el-menu-item>
    </template>

    <el-sub-menu
      v-else
      :index="resolvePath(item.path)"
    >
      <!-- popper-append-to-body -->
      <template #title>
        <i v-if="item.meta && item.meta.icon" class="iconfont" :class="item.meta.icon" :title="(isCollapse && isFirstLevel) ? item.meta.title : ''"></i>
        <span v-if="item.meta && item.meta.title"> {{ item.meta.title }}</span>
      </template>

      <template v-if="item.children && item.children.length">
        <sidebar-item
          v-for="child in item.children"
          :key="child.meta.menuId"
          :item="child"
          :is-collapse="false"
          :is-first-level="false"
          :menu-level="menuLevel + 1"
          :base-path="resolvePath(child.path)"
          class="nest-menu"
        />
      </template>
    </el-sub-menu>
  </div>
</template>

<script lang="ts">
// import path from 'path'
import { computed, defineComponent, PropType } from 'vue'
import { RouteRecordRaw, useRouter } from 'vue-router'
import { isExternal } from '@/utils/validate'
import { useI18n } from 'vue-i18n'

// Simple path join function to replace path.resolve
const joinPath = (basePath: string, routePath: string) => {
  if (!basePath) return routePath
  if (!routePath) return basePath
  const base = basePath.endsWith('/') ? basePath.slice(0, -1) : basePath
  const route = routePath.startsWith('/') ? routePath.slice(1) : routePath
  return `${base}/${route}`
}

export default defineComponent({
  name: 'SidebarItem',
  props: {
    item: {
      type: Object as PropType<RouteRecordRaw>,
      required: true
    },
    isCollapse: {
      type: Boolean,
      required: false,
      default: false
    },
    isFirstLevel: {
      type: Boolean,
      required: false,
      default: true
    },
    menuLevel: {
      type: Number,
      required: false,
      default: 1
    },
    basePath: {
      type: String,
      required: true
    }
  },
  setup(props) {
    const router = useRouter()
    const alwaysShowRootMenu = computed(() => {
      if (props.item.meta && props.item.meta.alwaysShow) {
        return true
      } else {
        return false
      }
    })

    const showingChildNumber = computed(() => {
      if (props.item.children) {
        const showingChildren = props.item.children.filter((item) => {
          if (item.meta && item.meta.hidden) {
            return false
          } else {
            return true
          }
        })
        return showingChildren.length
      }
      return 0
    })

    const theOnlyOneChild = computed(() => {
      if (showingChildNumber.value > 1) {
        return null
      }

      if (props.item.children && !props.item.children.length) {
        for (const child of props.item.children) {
          if (!child.meta || !child.meta.hidden) {
            return child
          }
        }
      }
      // If there is no children, return itself with path removed,
      // because this.basePath already contains item's path information
      return { ...props.item, path: '' }
    })

    const resolvePath = (routePath: string) => {
      if (isExternal(routePath)) {
        return routePath
      }
      if (routePath.startsWith('/')) {
        return routePath
      }
      if (isExternal(props.basePath)) {
        return props.basePath
      }
      return joinPath(props.basePath, routePath)
    }

    const handleMenuClick = (path: string) => {
      router.push(path).catch((err) => {
        console.log(err)
      })
    }

    const { t } = useI18n()

    return {
      t,
      alwaysShowRootMenu,
      showingChildNumber,
      theOnlyOneChild,
      resolvePath,
      handleMenuClick
    }
  }
})
</script>

<style lang="scss">
.expand-mode {
  &.first-level {
    & > .el-sub-menu > .el-sub-menu__title,
    & > .el-menu-item {
      font-weight: 600 !important;
      color: var(--ink) !important;

      span {
        font-weight: 600 !important;
        color: var(--ink) !important;
      }
    }
  }

  &.menu-level-2 {
    & > .el-sub-menu > .el-sub-menu__title,
    & > .el-menu-item {
      padding: 0 20px 0 36px !important;
    }
  }

  &.menu-level-3 {
    & > .el-sub-menu > .el-sub-menu__title,
    & > .el-menu-item {
      padding: 0 20px 0 52px !important;
    }
  }

  &.menu-level-4 {
    & > .el-sub-menu > .el-sub-menu__title,
    & > .el-menu-item {
      padding: 0 20px 0 68px !important;
    }
  }

  .el-sub-menu {
    background-color: transparent !important;
    overflow: hidden;

    & > .el-sub-menu__title {
      background-color: transparent !important;
      color: var(--ink) !important;

      .el-sub-menu__icon-arrow {
        display: block;
      }
    }

    &.is-active > .el-sub-menu__title {
      color: var(--blue) !important;
      background-color: transparent !important;

      i, .iconfont, span {
        color: var(--blue) !important;
      }
    }
  }

  .el-sub-menu__title {
    height: 50px !important;
    line-height: 50px !important;
    padding: 0 16px !important;
    color: var(--ink) !important;
    background-color: transparent !important;
    display: flex;
    align-items: center;

    i, .iconfont {
      margin-right: 12px;
      font-size: 16px;
      color: inherit;
      flex-shrink: 0;
    }

    span {
      margin-left: 0;
      color: inherit;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    &:hover {
      color: var(--blue) !important;
      background: var(--hover-bg) !important;
    }
  }

  .el-menu-item {
    height: 40px;
    line-height: 40px;
    padding: 0 16px !important;
    color: var(--ink) !important;
    background-color: transparent !important;
    display: flex;
    align-items: center;

    i, .iconfont {
      margin-right: 12px;
      font-size: 16px;
      color: inherit;
      flex-shrink: 0;
    }

    span {
      margin-left: 0;
      color: inherit;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    & > span {
      display: inline-block;
      padding-left: 0;
    }

    &:hover {
      color: var(--blue) !important;
      background: var(--hover-bg) !important;
    }

    &.is-active {
      color: #ffffff !important;
      background-color: var(--blue) !important;

      i, .iconfont, span {
        color: #ffffff !important;
      }
    }
  }
}

.collapse-mode {
  .el-sub-menu__title {
    height: 50px !important;
    line-height: 50px !important;
    padding: 0 !important;
    width: 54px !important;
    min-width: 54px !important;
    display: flex !important;
    justify-content: center !important;
    align-items: center !important;
    text-align: center !important;
    color: var(--ink) !important;
    background-color: transparent !important;

    i, .iconfont, svg {
      margin: 0 !important;
      padding: 0 !important;
      display: inline-flex !important;
      justify-content: center !important;
      align-items: center !important;
      font-size: 16px !important;
      width: 16px !important;
      height: 16px !important;
      line-height: 16px !important;
      text-align: center !important;
      flex-shrink: 0;
    }

    span {
      display: none !important;
    }

    .el-sub-menu__icon-arrow {
      display: none !important;
    }

    &:hover {
      color: var(--blue) !important;
      background: var(--hover-bg) !important;
    }
  }

  .el-sub-menu.is-active {
    background-color: transparent !important;

    > .el-sub-menu__title {
      background-color: transparent !important;
      color: var(--blue) !important;

      i, .iconfont {
        color: var(--blue) !important;
      }
    }
  }

  .el-menu-item {
    height: 40px !important;
    line-height: 40px !important;
    padding: 0 !important;
    width: 54px !important;
    min-width: 54px !important;
    display: flex !important;
    justify-content: center !important;
    align-items: center !important;
    text-align: center !important;
    color: var(--ink) !important;
    background-color: transparent !important;

    i, .iconfont, svg {
      margin: 0 !important;
      padding: 0 !important;
      display: inline-flex !important;
      justify-content: center !important;
      align-items: center !important;
      font-size: 16px !important;
      width: 16px !important;
      height: 16px !important;
      line-height: 16px !important;
      text-align: center !important;
      flex-shrink: 0;
    }

    span {
      display: none !important;
    }

    &:hover {
      color: var(--blue) !important;
      background: var(--hover-bg) !important;
    }

    &.is-active {
      color: #ffffff !important;
      background-color: var(--blue) !important;

      i, .iconfont {
        color: #ffffff !important;
      }
    }
  }

  &.first-level {
    .submenu-title-noDropdown {
      padding: 0 !important;
      position: relative;
      .el-tooltip {
        padding: 0 !important;
      }
    }
    .el-sub-menu {
      overflow: hidden;
      & > .el-sub-menu__title {
        .el-sub-menu__icon-arrow {
          display: none !important;
        }
        & > span {
          display: none !important;
        }
      }
    }
  }
}

.el-menu--vertical {
  .nest-menu {
    .el-menu-item,
    .el-sub-menu__title {
      height: 40px;
      line-height: 40px;
      padding: 0 20px 0 36px !important;
      font-weight: 400;
      color: var(--ink-muted) !important;
      background-color: transparent !important;

      span {
        font-weight: 400;
        color: inherit;
      }

      &:hover {
        color: var(--blue) !important;
        background: var(--hover-bg) !important;
      }

      &.is-active {
        font-weight: 500;
        color: #ffffff !important;
        background-color: var(--blue) !important;

        span {
          color: #ffffff !important;
        }
      }
    }

    // 三级菜单：右移缩进至 52px（+16px）
    &.menu-level-3,
    .menu-level-3,
    .nest-menu {
      & > .el-sub-menu > .el-sub-menu__title,
      & > .el-menu-item {
        padding: 0 20px 0 52px !important;
      }

      // 四级及更深层级菜单（52px -> 68px）
      &.menu-level-4,
      .menu-level-4,
      .nest-menu {
        & > .el-sub-menu > .el-sub-menu__title,
        & > .el-menu-item {
          padding: 0 20px 0 68px !important;
        }
      }
    }
  }
}
</style>
